/**
 * Capacités d'affichage alignées sur l'enum backend.
 * La classe MML (poids + volume) reste la référence pour l'estimation.
 */

export const VEHICLE_TYPES = [
  { id: "car",          label: "Voiture",                          max_weight_kg: 50,   max_volume_m3: 0.2, toll: 0.10 },
  { id: "van",          label: "Fourgon / utilitaire",             max_weight_kg: 800,  max_volume_m3: 20,  toll: 0.15 },
  { id: "truck",        label: "Petit camion porteur",             max_weight_kg: 3000, max_volume_m3: 30,  toll: 0.20 },
  { id: "heavy_truck",  label: "Camion porteur",                   max_weight_kg: 6000, max_volume_m3: 40,  toll: 0.25 },
];

export const VEHICLE_LABEL = Object.fromEntries(VEHICLE_TYPES.map((v) => [v.id, v.label]));

/** Grille MML — 6 classes A–F (Annexe C) : classées par poids ET volume. */
export const MML_CLASSES = [
  { id: "A", label: "A — Voiture",       max_weight_kg: 50,   max_volume_m3: 0.2, interurban: 0.75, urban: 1.00, depart: 10, minimum: 30, waitingRate: 30 },
  { id: "B", label: "B — Petit VUL",     max_weight_kg: 150,  max_volume_m3: 1,   interurban: 0.95, urban: 1.25, depart: 15, minimum: 40, waitingRate: 30 },
  { id: "C", label: "C — VUL",           max_weight_kg: 500,  max_volume_m3: 6,   interurban: 1.20, urban: 1.60, depart: 20, minimum: 55, waitingRate: 40 },
  { id: "D", label: "D — Grand VUL",     max_weight_kg: 800,  max_volume_m3: 20,  interurban: 1.45, urban: 1.90, depart: 25, minimum: 80, waitingRate: 40 },
  { id: "E", label: "E — Petit porteur", max_weight_kg: 3000, max_volume_m3: 30,  interurban: 1.85, urban: 2.45, depart: 40, minimum: 140, waitingRate: 60 },
  { id: "F", label: "F — Porteur",       max_weight_kg: 6000, max_volume_m3: 40,  interurban: 2.20, urban: 2.90, depart: 60, minimum: 180, waitingRate: 60 },
];

const CLASS_TO_VEHICLE = { A: "car", B: "van", C: "van", D: "van", E: "truck", F: "heavy_truck" };
const FREE_WAITING_MIN = 30;
const TVA_RATE = 0.20;
const COMMISSION_RATE = 0.15;

/** Retourne la 1re classe MML compatible poids ET volume, ou null si hors gabarit. */
export function classifyMMLClass(weightKg, volumeM3) {
  const w = Number(weightKg) || 0, v = Number(volumeM3) || 0;
  return MML_CLASSES.find((c) => w <= c.max_weight_kg && v <= c.max_volume_m3) || null;
}

export const ZONE_OPTIONS = [
  { value: "countryside", label: "Campagne / route" },
  { value: "city",        label: "Urbain dense" },
  { value: "mixed",       label: "Mixte (moyenne)" },
];

export function getVehicleById(id) {
  return VEHICLE_TYPES.find((v) => v.id === id);
}

/** Retourne le véhicule le plus léger capable de transporter poids ET volume. */
export function suggestVehicleByWeightVolume(weightKg, volumeM3 = 0) {
  if (!weightKg || weightKg <= 0) return VEHICLE_TYPES[0];
  return VEHICLE_TYPES.find((v) =>
    v.max_weight_kg >= Number(weightKg) && v.max_volume_m3 >= Number(volumeM3 || 0)
  ) || VEHICLE_TYPES[VEHICLE_TYPES.length - 1];
}

// Compatibilité conservée pour les appels historiques qui ne fournissent que le poids.
export const suggestVehicleByWeight = suggestVehicleByWeightVolume;

/** Vrai si le véhicule peut transporter simultanément poids et volume. */
export function isVehicleCompatible(vehicleId, weightKg, volumeM3 = 0) {
  const v = getVehicleById(vehicleId);
  if (!v) return false;
  return v.max_weight_kg >= Number(weightKg || 0) && v.max_volume_m3 >= Number(volumeM3 || 0);
}

function round2(value) {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

function localDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const raw = String(value);
  // A date-only ISO string is parsed as UTC by browsers; parse it locally so
  // the weekend/holiday surcharge follows the displayed French date.
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function easterSunday(year) {
  // Gregorian computus (same national calendar used by the backend).
  const a = year % 19, b = Math.floor(year / 100), c = year % 100;
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
  const month = Math.floor((a + 11 * h + 22 * l) / 451);
  const monthNumber = Math.floor((h + l - 7 * month + 114) / 31);
  const day = ((h + l - 7 * month + 114) % 31) + 1;
  return new Date(year, monthNumber - 1, day);
}

function dateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function frenchPublicHolidays(year) {
  const fixed = [[1, 1], [5, 1], [5, 8], [7, 14], [8, 15], [11, 1], [11, 11], [12, 25]];
  const keys = new Set(fixed.map(([month, day]) => dateKey(new Date(year, month - 1, day))));
  const easter = easterSunday(year);
  [1, 39, 50].forEach((offset) => {
    const day = new Date(easter);
    day.setDate(day.getDate() + offset);
    keys.add(dateKey(day));
  });
  return keys;
}

function surchargeFor(when) {
  const date = localDate(when);
  if (!date) return { rate: 0, label: "" };
  const hour = date.getHours();
  const day = date.getDay(); // 0 Sunday, 6 Saturday
  const night = hour >= 21 || hour < 6;
  if (frenchPublicHolidays(date.getFullYear()).has(dateKey(date))) return { rate: 0.25, label: "jour férié +25 %" };
  if (day === 0) return { rate: 0.25, label: "dimanche +25 %" };
  if (day === 6) return night ? { rate: 0.20, label: "nuit +20 %" } : { rate: 0.10, label: "samedi +10 %" };
  return night ? { rate: 0.20, label: "nuit +20 %" } : { rate: 0, label: "" };
}

/**
 * Calcul miroir de backend/core/pricing.py : B / N / P, TVA indicative,
 * péage, attente et majoration. Le prix reste librement modifiable.
 * @returns {{ total, ratePerKm, tollPerKm, baseRate, vehicleLabel, distanceKm } | null}
 */
export function computeSuggestedPrice({
  weightKg, volumeM3, distanceKm, vehicleType, zone = "mixed", includeToll = true,
  when = null, urbanKm = null, interurbanKm = null, waitingMinutes = 0, tollsEur = null,
}) {
  const distance = Number(distanceKm) || 0;
  if (distance <= 0 || !vehicleType || !isVehicleCompatible(vehicleType, weightKg, volumeM3)) return null;
  const cls = classifyMMLClass(weightKg, volumeM3);
  const vehicle = getVehicleById(vehicleType);
  if (!cls || !vehicle) return null;

  let urban = Math.max(0, Number(urbanKm) || 0);
  let interurban = Math.max(0, Number(interurbanKm) || 0);
  if (urbanKm == null && interurbanKm == null) {
    if (zone === "city") urban = distance;
    else if (zone === "countryside") interurban = distance;
    else { urban = distance / 2; interurban = distance / 2; }
  }
  const covered = urban + interurban;
  if (covered <= 0) interurban = distance;
  const baseRate = covered > 0
    ? (cls.urban * urban + cls.interurban * interurban) / covered
    : cls.interurban;
  const baseBeforeExtras = cls.depart + cls.urban * urban + cls.interurban * interurban;
  const baseN = Math.max(cls.minimum, baseBeforeExtras);
  const surcharge = surchargeFor(when);
  const extraWaiting = Math.max(0, (Number(waitingMinutes) || 0) - FREE_WAITING_MIN);
  const waitingFee = round2(extraWaiting / 60 * cls.waitingRate);
  const tollPerKm = includeToll ? vehicle.toll : 0;
  const tolls = tollsEur == null ? tollPerKm * distance : Math.max(0, Number(tollsEur) || 0);
  const net = baseN * (1 + surcharge.rate) + waitingFee + tolls;
  const total = round2(net * (1 + TVA_RATE) / (1 - COMMISSION_RATE));
  const commission = round2(total * COMMISSION_RATE);
  return {
    total,
    ratePerKm: round2(baseRate + tollPerKm),
    tollPerKm: round2(tollPerKm),
    baseRate: round2(baseRate),
    vehicleLabel: vehicle.label,
    distanceKm: round2(distance),
    classId: cls.id,
    classLabel: cls.label,
    vehicleClassType: CLASS_TO_VEHICLE[cls.id],
    baseBeforeExtras: round2(baseBeforeExtras),
    baseN: round2(baseN),
    surchargeRate: surcharge.rate,
    surchargeLabel: surcharge.label,
    waitingFee,
    tolls: round2(tolls),
    commission,
    transporterAmount: round2(total - commission),
    tvaRate: TVA_RATE,
  };
}
